import { Component, h, Prop } from '@stencil/core';
import { DateUtils, PersonDoc, RfiDoc, TagData, TaskDoc, TaskRelationship, WorkflowState } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { TaskInfoModal } from '../../global/components/task-info-modal';
import { PersonInfoModal } from '../../components/concourse-person-selector/person-info-modal';

@Component({
  tag: 'rfi-details-card',
  styleUrls: ['../../global/styles/detail-card-list.css'],
})
export class RfiDetailsCard {
  @Prop() rfi: RfiDoc;
  @Prop() people: PersonDoc[] = [];
  @Prop() scheduleTasks: TaskDoc[] = [];
  @Prop() descriptionTextExpanded: boolean = false;
  @Prop() projectId: string;
  @Prop() hasField: (name: string) => boolean;

  @Prop() removeTaskClick: (relationship: TaskRelationship) => void;
  @Prop() toggleDescriptionText: () => void;

  get firstState(): WorkflowState {
    return this.rfi.states?.[0] || null;
  }

  getDescriptionText() {
    if (!this.rfi.description) return '';

    if (this.descriptionTextExpanded || this.rfi.description.length <= 100) {
      return this.rfi.description;
    }

    return this.rfi.description.substring(0, 100) + '...';
  }

  formatText(text: string) {
    return PersonInfoModal.stringToRichText(text, this.people, 'showPersonInfo');
  }

  showPersonInfo(personId: string) {
    const person = PeopleService.findPerson(this.people, personId);
    PersonInfoModal.presentModal(person);
  }

  render() {
    if (!this.rfi || !this.people.length) return <div />;

    const descriptionLength = this.rfi.description?.length || 0;
    return (
      <div class="full-height ion-padding-horizontal vertical-scroll cc-scrollbars">
        <ion-list class="ion-no-padding detail-card-list">
            <ion-item-group>
              <ion-item-divider>
                <ion-item-group>
                  <ion-card-header class="ion-no-padding">
                    <ion-card-title>General</ion-card-title>
                  </ion-card-header>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Subject</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.rfi.subject ?? '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Description</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>
                      <div class="ion-display-flex ion-flex-column">
                        <div innerHTML={this.formatText(this.getDescriptionText())}></div>
                        {descriptionLength > 100 && (
                          <ion-button class="ion-align-self-start ink-button" fill="clear" size="small" onClick={() => this.toggleDescriptionText()}>
                            {this.descriptionTextExpanded ? 'less' : 'more'}
                          </ion-button>
                        )}
                      </div>
                    </ion-label>
                  </ion-item>
                </ion-item-group>
                {this.hasField('question') && (
                  <ion-item-group>
                    <ion-item>
                      <ion-label>
                        <p class="typography-caption workflow-view__details-placeholder">Question</p>
                      </ion-label>
                    </ion-item>
                    <ion-item>
                      <ion-label innerHTML={this.formatText(this.rfi.question)}></ion-label>
                    </ion-item>
                  </ion-item-group>
                )}
                {this.hasField('suggestedAnswer') && (
                  <ion-item-group>
                    <ion-item>
                      <ion-label>
                        <p class="typography-caption workflow-view__details-placeholder">Suggested Answer</p>
                      </ion-label>
                    </ion-item>
                    <ion-item>
                      <ion-label innerHTML={this.formatText(this.rfi.suggested_answer)}></ion-label>
                    </ion-item>
                  </ion-item-group>
                )}
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Due Date</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.rfi.due_date ? DateUtils.formatIso(this.rfi.due_date) : '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
              </ion-item-divider>

              <ion-item-divider>
                <ion-item-group>
                  <ion-card-header class="ion-no-padding ion-padding-top">
                    <ion-card-title>People</ion-card-title>
                  </ion-card-header>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Assigned to</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>
                      <div class="ion-display-flex ion-flex-wrap">
                        {!this.firstState || !this.firstState.dueBy || this.firstState.dueBy.length === 0
                          ? '-'
                          : this.firstState.dueBy.map(personId => {
                              const person = PeopleService.findPerson(this.people, personId);
                              return person ? <person-chip key={personId} person={person} /> : null;
                            })}
                      </div>
                    </ion-label>
                  </ion-item>
                </ion-item-group>

                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Distribution</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>
                      <div class="ion-display-flex ion-flex-wrap">
                        {!this.rfi.distribution || this.rfi.distribution.length === 0
                          ? '-'
                          : this.rfi.distribution?.map(personId => {
                              const person = PeopleService.findPerson(this.people, personId);
                              return person ? <person-chip key={personId} person={person} /> : null;
                            })}
                      </div>
                    </ion-label>
                  </ion-item>
                </ion-item-group>
              </ion-item-divider>

              <ion-item-divider>
                <ion-item-group>
                  <ion-card-header class="ion-no-padding ion-padding-top">
                    <ion-card-title>Tasks and Tags</ion-card-title>
                  </ion-card-header>
                </ion-item-group>
                {this.hasField('tasks') && (
                  <ion-item-group>
                    <ion-item>
                      <ion-label>
                        <p class="typography-caption workflow-view__details-placeholder">Affected Tasks</p>
                      </ion-label>
                    </ion-item>
                    <ion-item>
                      <ion-label>
                        {!this.rfi.successors || this.rfi.successors.length === 0
                          ? '-'
                          : this.rfi.successors.map(relationship => {
                              const taskId = relationship.task_unique_id;
                              const task = this.scheduleTasks && this.scheduleTasks.length > 0 ? this.scheduleTasks.find(t => t.unique_id == taskId) : null;
                              return (
                                <ion-chip onClick={() => TaskInfoModal.presentModal(task)}>
                                  <ion-label>{task ? task.name : taskId}</ion-label>
                                  <ion-icon
                                    name="close-circle"
                                    onClick={e => {
                                      e.stopPropagation();
                                      this.removeTaskClick(relationship);
                                    }}
                                  ></ion-icon>
                                </ion-chip>
                              );
                            })}
                      </ion-label>
                    </ion-item>
                  </ion-item-group>
                )}
                {this.hasField('tags') && (
                  <ion-item-group>
                    <ion-item>
                      <ion-label>
                        <p class="typography-caption workflow-view__details-placeholder">Tags</p>
                      </ion-label>
                    </ion-item>
                    <ion-item>
                      <ion-label>
                        <div class="ion-display-flex ion-flex-wrap">
                          {!this.rfi.tags || this.rfi.tags.length === 0 ? '-' : this.rfi.tags?.map((tag: TagData) => <concourse-tag projectId={this.projectId} data={tag} />)}
                        </div>
                      </ion-label>
                    </ion-item>
                  </ion-item-group>
                )}
              </ion-item-divider>
              {this.hasField('priority') ||
                this.hasField('cost-code') ||
                this.hasField('cost-impact') ||
                (this.hasField('schedule-impact') && (
                  <ion-item-group>
                    <ion-card-header class="ion-no-padding ion-padding-top">
                      <ion-card-title>Priority & Impact</ion-card-title>
                    </ion-card-header>
                  </ion-item-group>
                ))}

              {this.hasField('priority') && (
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Priority</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.rfi.priority ?? '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
              )}
              {this.hasField('cost-code') && (
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Cost Code</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.rfi?.cost_code ? `${this.rfi.cost_code.code_number} - ${this.rfi.cost_code.division_name}` : '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
              )}
              {this.hasField('cost-impact') && (
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Cost Impact</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.rfi.cost_impact ?? '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
              )}
              {this.hasField('schedule-impact') && (
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Schedule Impact</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.rfi.schedule_impact ?? '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
              )}
            </ion-item-group>
          </ion-list>
        </div>

    );
  }
}
